import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "react-bootstrap/Button";
import Offcanvas from "react-bootstrap/Offcanvas";
import Form from "react-bootstrap/Form";
import { toast } from "sonner";
import { GetRequest } from "../../Utils/hooks/ApiHandler";
import { useNavigate } from "react-router-dom";
import useLocalStorageState from "use-local-storage-state";
import Select from "react-select";
import { customStyles } from "../../Utils/helpers/commonhelper";

const teamSchema = z.object({
  teamName: z.string().min(1, "Team name is required"),
  description: z.string().min(1, "Description is required"),
  status: z.enum(["active", "inactive"]).default("active"),
});

export const TeamForm = ({
  show,
  setShowUserForm,
  addTeam,
  setAddTeam,
  setAlreadySelectedTeam,
  onHide,
  onSubmitForm,
  editTeamId,
  initialData,
  loading,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    resolver: zodResolver(teamSchema),
    defaultValues: {
      teamName: "",
      description: "",
      status: "active",
    },
    mode: "onSubmit",
  });

  const [users, setUsers] = useState([]);
  const [pipelineOptions, setPipelineOptions] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedPipelines, setSelectedPipelines] = useState([]);
  const [statusValue, setStatusValue] = useState({ value: "active", label: "Active" });
  const [allSelected, setAllSelected] = useState(false);

  const navigate = useNavigate();
  const [customer, setCustomer] = useLocalStorageState("customer");
  const customerId = customer?.customer_details?.customer_id;
  const token = customer?.token?.authorization;
  const rf = customer?.token?.authentication;
  const headers = {
    authentication: rf,
    Authorization: token,
  };
  const extras = { customer, setCustomer, rf, navigate };

  // Fetch users and pipelines always on open
  useEffect(() => {
    const fetchData = async () => {
      const userRes = await GetRequest(`users/users/fetchAllUser/${customerId}`, { headers }, extras);
      const pipelineRes = await GetRequest(`lead/fetchPipelines/${customerId}`, { headers }, extras);

      const allUsers = userRes?.data?.map((user) => ({
        value: user.id,
        label: user.first_name + " " + user.last_name,
      })) || [];

      const allPipelines = pipelineRes?.data?.map((pipeline) => ({
        value: pipeline.id,
        label: pipeline.pipeline_name,
      })) || [];

      setUsers(allUsers);
      const fullPipelineOptions = [{ value: "select_all", label: allSelected ? "Unselect All" : "Select All" }, ...allPipelines];
      setPipelineOptions(fullPipelineOptions);

      if (initialData && initialData.length > 0) {
        const team = initialData[0];
        setValue("teamName", team.team_name || "");
        setValue("description", team.description || "");
        setValue("status", team.status?.toLowerCase() || "active");

        setStatusValue(
          team.status?.toLowerCase() === "inactive"
            ? { value: "inactive", label: "Inactive" }
            : { value: "active", label: "Active" }
        );

        const selectedMemberIds = team.members?.map((m) => m.user_id) || [];
        const matchedMembers = allUsers.filter((user) => selectedMemberIds.includes(user.value));
        setSelectedMembers(matchedMembers);

        const selectedPipelineIds = team.pipeline?.map((p) => p.pipeline_id) || [];
        const matchedPipelines = allPipelines.filter((pipe) => selectedPipelineIds.includes(pipe.value));
        setSelectedPipelines(matchedPipelines);

        setAllSelected(matchedPipelines.length === allPipelines.length);
      } else {
        reset();
        setStatusValue({ value: "active", label: "Active" });
        setSelectedMembers([]);
        setSelectedPipelines([]);
        setAllSelected(false);
      }
    };

    if (show) fetchData();
  }, [show, initialData]);

  const handlePipelineChange = (selected) => {
    if (!selected) return;
    const isSelectAllClicked = selected.find((opt) => opt.value === "select_all");
    const allRealOptions = pipelineOptions.filter((opt) => opt.value !== "select_all");

    if (isSelectAllClicked) {
      if (!allSelected) {
        setSelectedPipelines(allRealOptions);
        setAllSelected(true);
        setPipelineOptions([{ value: "select_all", label: "Unselect All" }, ...allRealOptions]);
      } else {
        setSelectedPipelines([]);
        setAllSelected(false);
        setPipelineOptions([{ value: "select_all", label: "Select All" }, ...allRealOptions]);
      }
    } else {
      setSelectedPipelines(selected);
      setAllSelected(selected.length === allRealOptions.length);
    }
  };

  const onSubmit = async (data) => {
    try {
      const formattedData = {
        customer_id: customer?.customer_details.customer_id,
        members_id: selectedMembers.map((m) => m.value),
        pipeline_id: selectedPipelines.map((p) => p.value),
        visibility: allSelected ? "all" : "team",
        team_name: data.teamName,
        description: data.description,
        user_login_id: customer?.customer_details.id,
        status: data.status,
        ...(editTeamId && { id: editTeamId }),
      };

      const response = await onSubmitForm(formattedData);
      if (response?.status === "success") {
        // toast.success("Team saved successfully");

        if (addTeam) {
          const newTeamId = response.data.id || response.data;
          setAlreadySelectedTeam((prev) => [...(Array.isArray(prev) ? prev : []), newTeamId.toString()]);
          setAddTeam(false);
        }

        reset();
        setStatusValue({ value: "active", label: "Active" });
        setSelectedMembers([]);
        setSelectedPipelines([]);
        setAllSelected(false);
        onHide();
      } else {
        toast.error(response?.message || "Failed to save team");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Something went wrong");
    }
  };

  const handleClose = () => {
    reset();
    onHide();
    if (addTeam) {
      setAddTeam(false);
      setShowUserForm(true);
    }
  };

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  return (
    <Offcanvas show={show} onHide={handleClose} placement="end" backdrop="static" scroll className="team-offcanvas">

      <Offcanvas.Header
        closeButton
        className="border-bottom border-dashed pt-3"
      >
        <div className="d-flex align-items-center gap-2">
          <div className="pt-0 ps-4">
            <Offcanvas.Title
              style={{ color: "#0791ff" }}
              className="addEnquiryHead"
            >  {editTeamId ? "Edit Team" : "Add Team"}
            </Offcanvas.Title>
            <p
              style={{ fontSize: "14px", color: "#b26792", lineHeight: 1.1 }}
              className=" text-secondary text-nowrap mb-0"
            >
              {/* Create New Contact For Future */}
            </p>
          </div>
        </div>
      </Offcanvas.Header>
      <Offcanvas.Body className="d-flex flex-column">
        <Form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column gap-3 flex-grow-1  form-container">
          <Form.Group className="mb-1 focus-z-index position-relative ">
            <Form.Label>Team Name <span className="text-danger">*</span></Form.Label>
            <Form.Control {...register("teamName")} placeholder="e.g. Developers" />
            {errors.teamName && <p className="text-danger mt-1" style={{ fontSize: "12px" }}>{errors.teamName.message}</p>}
          </Form.Group>

          <Form.Group className="mb-1 focus-z-index position-relative ">
            <Form.Label>Select Team Members</Form.Label>
            <Select
              isMulti
              options={users}
              classNamePrefix="react-select"
              styles={customStyles}
              value={selectedMembers}
              onChange={(selected) => setSelectedMembers(selected || [])}
              placeholder="Choose members..."
            />
          </Form.Group>

          <Form.Group className="mb-1 focus-z-index position-relative ">
            <Form.Label>Select Pipelines</Form.Label>
            <Select
              options={pipelineOptions}
              isMulti
              value={selectedPipelines}
              classNamePrefix="react-select"
              styles={customStyles}
              onChange={handlePipelineChange}
              placeholder="Choose pipelines..."
            />
          </Form.Group>

          <Form.Group className="mb-1 focus-z-index position-relative ">
            <Form.Label>Description <span className="text-danger">*</span></Form.Label>
            <Form.Control as="textarea" {...register("description")} placeholder="Team description..." style={{ minHeight: "120px" }} />
            {errors.description && <p className="text-danger mt-1" style={{ fontSize: "12px" }}>
              {errors.description.message}</p>}
          </Form.Group>

          <Form.Group className="mb-1 focus-z-index position-relative ">
            <Form.Label>Status</Form.Label>
            <Select
              options={statusOptions}
              value={statusValue}
              classNamePrefix="react-select"
              styles={customStyles}
              onChange={(selected) => {
                setValue("status", selected.value, { shouldValidate: true });
                setStatusValue(selected);
              }}
            />
            {errors.status && <p className="text-danger small-error">{errors.status.message}</p>}
          </Form.Group>

          <div className="d-flex justify-content-end gap-2 mt-auto mb-2 me-0">
            <Button type="button" className="btn btn-default-outline" onClick={handleClose}>Cancel</Button>
            <Button type="submit" className="btn btn-default" disabled={loading}>
              {loading ? <div className="spinner-border spinner-border-sm" role="status" /> : editTeamId ? "Update" : "Add"} Team
            </Button>
          </div>
        </Form>
      </Offcanvas.Body>
    </Offcanvas>
  );
};